// modules/social-accounts/AccountsPage.tsx
// Сторінка: форма підключення + список підключених акаунтів + кнопка синхронізації

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AccountForm } from './AccountForm.tsx';
import { useAccounts } from './useAccounts.ts';
import { useAssets } from '../ad-assets/useAssets.ts';
import type { SocialAccount } from '../../core/types.ts';

// Стан синхронізації для одного акаунту
interface SyncState {
  loading: boolean;
  lastSyncAt: string | null;
  lastStatus: 'ok' | 'error' | 'rate_limited' | null;
  message: string | null;
}

export function AccountsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { accounts, loading, error, fetchAccounts, deleteAccount } = useAccounts();
  const { syncAccount, fetchSyncLogs, syncLogs } = useAssets();

  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Стан синхронізації для кожного акаунту (key = account.id)
  const [syncStates, setSyncStates] = useState<Record<string, SyncState>>({});

  // Обробка redirect після OAuth callback
  useEffect(() => {
    const connected = searchParams.get('connected');
    const accountName = searchParams.get('account_name');
    const oauthError = searchParams.get('oauth_error');

    if (connected === '1') {
      const name = accountName ? decodeURIComponent(accountName) : 'Акаунт';
      showNotification('success', `${name} успішно підключений! Синхронізація запущена у фоні.`);
      fetchAccounts();
      setSearchParams({}, { replace: true });
    } else if (oauthError) {
      const msg = decodeURIComponent(oauthError);
      showNotification('error', `Помилка OAuth: ${msg}`);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, fetchAccounts, setSearchParams]);

  // Завантажити початковий стан синхронізації для всіх акаунтів
  useEffect(() => {
    // Завантажуємо sync logs для першого акаунту при ініціалізації;
    // детально — в handleSyncClick
  }, [accounts]);

  function showNotification(type: 'success' | 'error', message: string) {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  }

  async function handleDelete(account: SocialAccount) {
    if (!window.confirm(`Видалити акаунт "${account.name ?? account.id}"? Всі пов'язані дані (кабінети, сторінки, пости) будуть видалені.`)) {
      return;
    }
    setDeletingId(account.id);
    const ok = await deleteAccount(account.id);
    setDeletingId(null);
    if (ok) {
      showNotification('success', `Акаунт "${account.name ?? account.id}" видалено`);
    }
  }

  const handleSync = useCallback(async (account: SocialAccount) => {
    setSyncStates((prev) => ({
      ...prev,
      [account.id]: { loading: true, lastSyncAt: prev[account.id]?.lastSyncAt ?? null, lastStatus: prev[account.id]?.lastStatus ?? null, message: null },
    }));

    const result = await syncAccount(account.id);

    if (result.ok) {
      const parts: string[] = [];
      if (result.adAccountsCount) parts.push(`${result.adAccountsCount} кабінетів`);
      if (result.pagesCount) parts.push(`${result.pagesCount} сторінок`);
      if (result.postsCount) parts.push(`${result.postsCount} постів`);

      setSyncStates((prev) => ({
        ...prev,
        [account.id]: {
          loading: false,
          lastSyncAt: new Date().toISOString(),
          lastStatus: 'ok',
          message: parts.length > 0 ? `Синхронізовано: ${parts.join(', ')}` : 'Синхронізовано',
        },
      }));
      showNotification('success', `Акаунт "${account.name ?? account.id}" синхронізовано`);
    } else {
      setSyncStates((prev) => ({
        ...prev,
        [account.id]: {
          loading: false,
          lastSyncAt: prev[account.id]?.lastSyncAt ?? null,
          lastStatus: 'error',
          message: result.error ?? 'Помилка синхронізації',
        },
      }));
      showNotification('error', `Помилка синхронізації: ${result.error ?? 'невідома помилка'}`);
    }
  }, [syncAccount]);

  function getInitials(name: string | null): string {
    if (!name) return '?';
    return name
      .split(' ')
      .map((w) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  function formatDate(iso: string): string {
    try {
      return new Intl.DateTimeFormat('uk-UA', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  }

  function formatExpiry(iso: string | null): string | null {
    if (!iso) return null;
    try {
      const date = new Date(iso);
      const now = new Date();
      const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays < 0) return 'Токен протух';
      if (diffDays <= 7) return `Спливає через ${diffDays}д`;
      return `До ${formatDate(iso)}`;
    } catch {
      return null;
    }
  }

  return (
    <div className="page-body">
      {/* Заголовок сторінки */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h1 style={{ fontSize: '1.5rem', marginBottom: 'var(--space-1)' }}>
          Facebook Акаунти
        </h1>
        <p className="text-muted text-sm">
          Керуй підключеними Meta-акаунтами. Авторизація відбувається вручну.
        </p>
      </div>

      {/* Нотифікація після OAuth */}
      {notification && (
        <div
          className={`notification notification--${notification.type}`}
          style={{ marginBottom: 'var(--space-5)' }}
        >
          {notification.message}
        </div>
      )}

      {/* Форма підключення */}
      <AccountForm onUrlGenerated={() => {}} />

      {/* Список акаунтів */}
      <section>
        <div className="flex items-center justify-between" style={{ marginBottom: 'var(--space-4)' }}>
          <h2 style={{ fontSize: '1rem' }}>
            Підключені акаунти
            {accounts.length > 0 && (
              <span
                className="text-subtle font-mono text-sm"
                style={{ marginLeft: 'var(--space-2)' }}
              >
                ({accounts.length})
              </span>
            )}
          </h2>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={fetchAccounts}
            disabled={loading}
            title="Оновити список"
          >
            {loading ? 'Оновлення...' : 'Оновити'}
          </button>
        </div>

        {/* Помилка завантаження */}
        {error && !loading && (
          <div className="notification notification--error" style={{ marginBottom: 'var(--space-4)' }}>
            <span>{error}</span>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div className="accounts-grid">
            {[1, 2, 3].map((i) => (
              <div key={i} className="glass-card account-card">
                <div className="account-card__header">
                  <div className="skeleton" style={{ width: 44, height: 44, borderRadius: 'var(--radius-md)' }} />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div className="skeleton" style={{ height: 16, width: '70%' }} />
                    <div className="skeleton" style={{ height: 12, width: '50%' }} />
                  </div>
                </div>
                <div className="skeleton" style={{ height: 28, borderRadius: 'var(--radius-full)', width: 80 }} />
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && accounts.length === 0 && !error && (
          <div className="glass-card empty-state">
            <div className="empty-state__title">Немає підключених акаунтів</div>
            <div className="empty-state__desc">
              Введи ім'я акаунта вище, отримай OAuth URL і пройди авторизацію у Facebook.
            </div>
          </div>
        )}

        {/* Список карток */}
        {!loading && accounts.length > 0 && (
          <div className="accounts-grid">
            {accounts.map((account) => {
              const isActive = account.status === 'active';
              const expiryText = formatExpiry(account.tokenExpiresAt);
              const syncState = syncStates[account.id];

              return (
                <div
                  key={account.id}
                  className={`glass-card account-card ${isActive ? 'glass-card--active' : ''}`}
                >
                  {/* Header */}
                  <div className="account-card__header">
                    <div className="account-card__avatar">
                      {getInitials(account.name)}
                    </div>
                    <div className="account-card__info">
                      <div className="account-card__name" title={account.name ?? account.id}>
                        {account.name ?? '—'}
                      </div>
                    </div>
                  </div>

                  {/* Status badge */}
                  <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                    <span className={`badge badge--${account.status}`}>
                      {account.status === 'active' && 'Активний'}
                      {account.status === 'expired' && 'Протух'}
                      {account.status === 'disconnected' && 'Відключений'}
                    </span>

                    {expiryText && (
                      <span className="text-xs text-subtle">{expiryText}</span>
                    )}
                  </div>

                  {/* FB User ID */}
                  <div
                    className="text-xs font-mono text-subtle"
                    style={{ marginTop: 'var(--space-2)' }}
                    title={`Facebook User ID: ${account.fbUserId ?? account.id}`}
                  >
                    fb: {account.fbUserId ?? account.id}
                  </div>

                  {/* Sync status */}
                  {syncState && !syncState.loading && syncState.message && (
                    <div
                      className={`text-xs ${syncState.lastStatus === 'ok' ? 'text-muted' : 'text-xs'}`}
                      style={{
                        marginTop: 'var(--space-2)',
                        color: syncState.lastStatus === 'ok'
                          ? 'var(--color-success)'
                          : syncState.lastStatus === 'rate_limited'
                          ? 'var(--color-warning)'
                          : 'var(--color-error)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-1)',
                      }}
                    >
                      {syncState.lastStatus === 'ok' && (
                        <span style={{ fontSize: '0.75rem' }}>Sync:</span>
                      )}
                      {syncState.message}
                      {syncState.lastSyncAt && (
                        <span className="text-subtle" style={{ marginLeft: 4 }}>
                          · {formatDate(syncState.lastSyncAt)}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Footer: sync + delete */}
                  <div className="account-card__actions">
                    <span className="account-card__date">
                      {formatDate(account.connectedAt)}
                    </span>
                    <div className="flex items-center gap-2">
                      {/* Sync button */}
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => handleSync(account)}
                        disabled={syncState?.loading}
                        title="Синхронізувати кабінети, сторінки і пости"
                      >
                        {syncState?.loading ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span
                              className="spinner"
                              style={{
                                width: 12,
                                height: 12,
                                border: '2px solid var(--color-border)',
                                borderTopColor: 'var(--color-primary)',
                                borderRadius: '50%',
                                animation: 'spin 0.6s linear infinite',
                                display: 'inline-block',
                              }}
                            />
                            Синхр...
                          </span>
                        ) : (
                          'Синхронізувати'
                        )}
                      </button>

                      {/* Delete button */}
                      <button
                        type="button"
                        className="btn btn--danger btn--sm"
                        onClick={() => handleDelete(account)}
                        disabled={deletingId === account.id}
                        title="Видалити акаунт"
                      >
                        {deletingId === account.id ? 'Видалення...' : 'Видалити'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
